import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageTitle } from '../../../../_metronic/layout/core';
import FirebaseCalendar from './FirebaseCalendar';
import { CalendarView, toLocalDateString } from '../../../types/calendar';

const calendarBreadCrumbs = [
  { title: 'Dashboard', path: '/dashboard', isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
];

/**
 * The single mount point for the calendar.
 *
 * Both hosts — this page and `src/app/modals/CalendarModal.tsx` — render THIS,
 * so there is exactly one place that knows how FirebaseCalendar is wired. The
 * page drives it from the URL; the modal renders it undriven and it keeps its
 * own internal anchor/view, which is why every prop here is optional.
 */
export interface CalendarSurfaceProps {
  supportOnly?: boolean;
  /** Which layout to render ('month' | 'week' | 'agenda'). */
  view?: CalendarView;
  /** Anchor day as a local 'YYYY-MM-DD'. */
  date?: string;
  onViewChange?: (view: CalendarView) => void;
  onDateChange?: (date: Date) => void;
}

// FirebaseCalendar is the provider wrapper (RepeatingInvoiceProvider +
// CalendarProvider) around FirebaseCalendarComponent. Its CalendarCardProps is
// the pass-through seam that forwards these four props down to the grid, which
// is what makes `?date=` / `?view=` actually drive it.
export const CalendarSurface: React.FC<CalendarSurfaceProps> = (props) => <FirebaseCalendar {...props} />;

/** Valid `?view=` values. Anything else falls back to 'month'. */
const CALENDAR_VIEWS: CalendarView[] = ['month', 'week', 'agenda'];

function parseView(raw: string | null): CalendarView {
  return CALENDAR_VIEWS.includes(raw as CalendarView) ? (raw as CalendarView) : 'month';
}

/**
 * Validate `?date=`.
 *
 * Shape-checked AND round-tripped through a LOCAL Date, so '2026-02-31' (which
 * Date happily rolls to 3 March) is rejected rather than silently landing the
 * user on a different day than the link says. Built from the three numbers
 * rather than `new Date(raw)` because the string form is parsed as UTC midnight
 * — in Adelaide (+9:30/+10:30) that is still the same local day, but the parse
 * is wrong on principle and this value travels into links people send.
 */
function parseDay(raw: string | null): string | null {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [year, month, day] = raw.split('-').map(Number);
  const probe = new Date(year, month - 1, day);
  if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) {
    return null;
  }
  return raw;
}

/** Floor so the grid is still usable on a short landscape phone. */
const MIN_SHELL_HEIGHT = 460;
/** Breathing room under the calendar so it doesn't butt into the footer. */
const SHELL_BOTTOM_GUTTER = 24;

/**
 * `.modern-calendar` is `height: 100%`, and on ≤768px the month grid is its own
 * scroll container with the day panel absolutely positioned inside it — so the
 * host has to hand it a definite height or the panel anchors to a page-tall
 * wrapper and lands below the fold.
 *
 * The height is measured rather than hardcoded because three different chromes
 * host this route (Metronic, Terminal, Graphite) and their headers are all
 * different heights; `calc(100dvh - <magic>)` would be wrong in at least two of
 * them. Setting a height on this element cannot move its own top, so there is
 * no measure/layout feedback loop. visualViewport is preferred over
 * innerHeight so an open mobile keyboard shrinks the calendar instead of
 * pushing it under the keyboard.
 */
function useAvailableHeight(ref: React.RefObject<HTMLElement>): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const viewport = window.visualViewport?.height ?? window.innerHeight;
      setHeight(Math.max(MIN_SHELL_HEIGHT, Math.round(viewport - top - SHELL_BOTTOM_GUTTER)));
    };

    measure();
    // Re-measure after paint: the breadcrumb/page title above this element
    // renders through PageDataProvider on the same tick, which shifts our top.
    const raf = requestAnimationFrame(measure);

    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [ref]);

  return height;
}

/**
 * `/calendar` — the routable calendar.
 *
 * The URL is the single source of truth for the view and the anchor day, so a
 * refresh, a bookmark, an Andrei reply or a notification can all point at one
 * specific day (`/calendar?date=2026-08-14&view=week`) and land on it.
 *
 * Both writes are `replace: true` on purpose: paging through months or flicking
 * between views is not "somewhere I navigated to", it is state of the page I am
 * already on. Stacking a history entry per month would mean Back had to be
 * pressed eleven times to leave the calendar — the exact class of complaint the
 * modal had (Back didn't close it). With replace, Back leaves the calendar and
 * the URL still describes what is on screen for refresh and copy-paste.
 */
const CalendarPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const shellRef = useRef<HTMLDivElement>(null);
  const shellHeight = useAvailableHeight(shellRef);

  // Derived straight off the URL rather than mirrored into state, so Back /
  // Forward and an externally-pushed link need no reconciliation effect.
  const view = parseView(searchParams.get('view'));
  const dateParam = parseDay(searchParams.get('date'));

  const handleViewChange = useCallback(
    (next: CalendarView) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('view', next);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleDateChange = useCallback(
    (next: Date) => {
      const day = toLocalDateString(next);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('date', day);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <>
      <PageTitle breadcrumbs={calendarBreadCrumbs}>Calendar</PageTitle>
      <div
        ref={shellRef}
        className="calendar-page"
        style={{
          // 72dvh only covers the first paint, before the measurement lands.
          height: shellHeight ? `${shellHeight}px` : '72dvh',
          minHeight: `${MIN_SHELL_HEIGHT}px`,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <CalendarSurface
          key={searchParams.get('scope') === 'support' ? 'support' : 'all'}
          supportOnly={searchParams.get('scope') === 'support'}
          view={view}
          date={dateParam ?? undefined}
          onViewChange={handleViewChange}
          onDateChange={handleDateChange}
        />
      </div>
    </>
  );
};

export default CalendarPage;
